import { forkJoin, Observable } from "rxjs";
import { map } from "rxjs/operators";
import * as _ from "lodash";
import { LgConsole } from "@logex/framework/core";

import {
    LoaderArguments as LoadManagerArguments,
    LoaderCallback as LoadManagerLoader,
    LoaderConfiguration as ILoadManagerLoader
} from "./configuration";
import { Loader } from "./loader";
import { Injectable, OnDestroy, inject } from "@angular/core";

/**
 * Important: LoadManager cannot be received by dependency injection, because it
 * should not be a singleton.
 */
@Injectable({ providedIn: "root" })
export class LoadManager implements OnDestroy {
    private _lgConsole = inject(LgConsole).withSource("Logex.Application.LoadManager");

    ngOnDestroy(): void {
        this.cancelAll();
    }

    // ----------------------------------------------------------------------------------
    // Fields
    private _loaders: _.Dictionary<Loader> = {};

    // ----------------------------------------------------------------------------------

    /**
     * Adds a named data loader.
     *
     * @param name Identifying name of the loader.
     * @param cfg The loader definition
     */
    addLoader(name: string, cfg: ILoadManagerLoader) {
        this._loaders[name] = new Loader(this, name, cfg, this._lgConsole);
    }

    /**
     * Adds a set of loaders at once.
     *
     * Usage example:
     * ```typescript
     * const firstReferenceId = () => this.firstReferenceInfo ? this.firstReferenceInfo.id : 999;
     * const secondReferenceId = () => this.secondReferenceInfo ? this.secondReferenceInfo.id : 999;
     * const filterArgs = {
     * 	specialism: () => this.selectedSpecialism
     * 		? [this.selectedSpecialism.specialisme]
     * 		: this.filter.getIntIds( "specialism" ),
     * 	diagnosisGroup: () => this.filter.getStringIds( "diagnosisGroup" ),
     * 	diagnosis: () => this.selectedDiagnosis
     * 		? [this.selectedDiagnosis.diagnose]
     * 		: this.filter.getStringIds( "diagnosis" ),
     * 	tariff: () => this.filter.getIntIds( "tariff" ),
     * 	headGroup: () => this.filter.getIntIds( "headGroup" ),
     * 	productGroup: () => this.filter.getIntIds( "productGroup" ),
     * 	product: () => this.selectedProduct
     * 		? [this.selectedProduct.zorgproduct]
     * 		: this.filter.getIntIds( "product" ),
     * 	productCluster: () => this.filter.getIntIds( "productCluster" ),
     * 	zpk: () => this.filter.getIntIds( "zpk" ),
     * 	activity: () => this.filter.getIntIds( "activity" ),
     * 	activityCategory: () => this.filter.getIntIds( "activityCategory" )
     * } as Logex.Application.LoadManagerArgumentsMap;
     *
     * ...
     *
     * this.loadManager.add( {
     *	productActivities: {
     * 		args: [firstReferenceId, secondReferenceId, filterArgs],
     * 		loader: ( firstReferenceId, secondReferenceId, filter ) => ...
     * ```
     *
     * @param loaders A dincitonary of loaders definitions, where field name will be used
     *    as a loader name.
     */
    add(loaders: _.Dictionary<ILoadManagerLoader | LoadManagerLoader>) {
        const loadersNormalized: _.Dictionary<ILoadManagerLoader> = _.mapValues(loaders, x => {
            if (_.isFunction(x)) {
                return {
                    loader: x
                } as ILoadManagerLoader;
            } else {
                return x as ILoadManagerLoader;
            }
        });
        _.each(loadersNormalized, (loader, name) => {
            this.addLoader(name, loader);
        });
    }

    /**
     * Load a set of loaders identified by names. If certain loader was already executed
     * with the same arguments that are actual now, it won't be executed second time.
     * So you can execute [[load]] as ofthen as necessary, it won't issue unneeded requests.
     *
     * If a loader is being executed at the time of issuing another load request, the first
     * loading will be cancelled.
     *
     * @param names Loaders to execute.
     */
    load(...names: string[]): Observable<any[]> {
        return this._doLoad(names, false);
    }

    /**
     * Execute one specific loader, using additional arguments. Given arguments do not fully replace
     * the arguments that were specified in the loader configuration, but rather get applied on top.
     * Since the loader is always only once, the result is also not an array like in [[load]] but just
     * a single object.
     *
     * @param name Name of the loader.
     * @param argsOverride Array of arguments in the same form that is used in loader configuration.
     *      If certain array item is specified (not undefined), then it can either replace the original
     *      (if it a function or constant) or be overlayed on top of the original argument (if it is a
     *      dictionary)
     */
    loadParamsOverride(name: string, argsOverride?: LoadManagerArguments) {
        return this._doLoad([name], false, argsOverride).pipe(map(([data]) => data));
    }

    /**
     * Acts just like [[load]], but doesn't check last loaded arguments. So that data sets
     * loaders would be executed unconditionally.
     *
     * @param names
     */
    reload(...names: string[]): Observable<any[]> {
        return this._doLoad(names, true);
    }

    /**
     * Execute all registered loaders. If something was already loaded, it will be skipped.
     */
    loadAll() {
        return this.load(..._.map(this._loaders, (loader, name) => name));
    }

    /**
     * Execute all registered loaders unconditionally.
     */
    reloadAll() {
        return this.reload(..._.map(this._loaders, (loader, name) => name));
    }

    private _getLoader(name: string) {
        const loader = this._loaders[name];
        if (loader) return loader;

        throw Error(`Unknown loader "${name}"`);
    }

    protected _doLoad(names: string[], forceReload: boolean, argsOverride?: LoadManagerArguments) {
        this._lgConsole.debug(`Request to load [${names.join(",")}]`);
        return forkJoin(
            _.map(names, name => {
                return this._getLoader(name).load(argsOverride, forceReload);
            })
        );
    }

    /**
     * Indicates that certain loader is being executed now. Useful to display loading
     * indicators.
     *
     * @param name Name of the loader to check.
     */
    isLoading(name: string): boolean {
        const loader = this._getLoader(name);
        return loader.isLoading;
    }

    isLoadingAsObservable(name: string): Observable<boolean> {
        const loader = this._getLoader(name);
        return loader.isLoadingAsObservable;
    }

    /**
     * Indicates that certain dataset was loaded at least once and is not loading right now.
     *
     * @param name
     */
    isLoaded(name: string): boolean {
        const loader = this._getLoader(name);
        return loader.isLoaded;
    }

    /**
     * Cancels the dataset loading if it is still active.
     *
     * @param name The dataset name to cancel.
     */
    cancel(name: string) {
        const loader = this._getLoader(name);
        loader.cancel();
    }

    cancelAll() {
        for (const key in this._loaders) {
            if (this._loaders[key].isLoading) this._loaders[key].cancel();
        }
    }

    /**
     * Gets last call's params for the given data set.
     *
     * @param name Name of the data set
     */
    getLastArgs(name: string) {
        const loader = this._getLoader(name);
        return loader.lastArgs;
    }

    /**
     * Clears the arguments for the last loaded data sets, so that the set will be reloaded upon the next request
     *
     * @param names Names of the loaders.
     */
    clearArgs(...names: string[]) {
        _.each(names, name => {
            const loader = this._getLoader(name);
            loader.clearLastArgs();
        });
    }

    /**
     * Cancel active requests and flushes cached loaders
     */
    flush() {
        _.each(this._loaders, x => {
            x.cancel();
            x.clearLastArgs();
        });
    }

    dataAsObservable(name: string): Observable<any> {
        const loader = this._getLoader(name);
        return loader.dataAsObservable();
    }

    getObservedLoaders(): string[] {
        return _.map(
            _.filter(this._loaders, x => x.isObserved()),
            x => x.name
        );
    }
}

export interface ILoadManager extends LoadManager {}
